import _ from 'lodash';
import React, { useEffect } from 'react';
import { batch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import type { UserAction } from '../../components/ActionButtons/ActionButtons';
import ActionButtons from '../../components/ActionButtons/ActionButtons';
import Loader from '../../components/Loader/Loader';
import type { DynamicFormFieldType } from '../../components/MemberSettingsDynamicForm/MemberSettingsDynamicForm';
import MemberSettingsDynamicForm from '../../components/MemberSettingsDynamicForm/MemberSettingsDynamicForm';
import { ProfilePicture } from '../../components/ProfilePicture/ProfilePicture';
import { useAppDispatch, useAppSelector } from '../../hooks';
import type { RootState } from '../../store';
import { palette } from '../../theme/colors';
import { getNumbersBasedDateTextByLanguage } from '../../utils/date';
import { ActiveTab } from '../../utils/enums';
import { SName } from '../GroupDetail/GroupDetail.styles';
import { selectMemberById } from '../GroupDetail/groupDetailSlice';
import { fetchGroupDetail } from '../GroupDetail/groupDetailSlice/actionCreators';
import LocationIcon from '../../assets/icons/location-marker-with-shadow--yellow.svg';
import NoLocationIcon from '../../assets/icons/location-marker-with-shadow--grey.svg';
import { selectImOkDocument } from '../ImOkList/imOkSlice';
import { fetchImOkDocument } from '../ImOkList/imOkSlice/actionCreators';
import {
  getIamOkMemberLocation,
  getIamOkMemberLocationName,
  handleMemberSettingsEmailClick,
  handleMemberSettingsPhoneClick,
  mapGroupMemberToSelectUser,
} from './helpers';
import { Modal } from '../../components/Modal/Modal';
import MapModal from '../../components/MapModal/MapModal';
import { fetchOnCallAlertDocument } from '../OnCallAlertList/onCallAlertSlice/actionCreators';
import { selectOnCallAlertDocument } from '../OnCallAlertList/onCallAlertSlice';
import { translate } from '../../utils/translate';
import { selectUserId } from '../Login/LoginSlice';
import { Page } from '../../components/Page/Page';
import { resetAll, setActiveTab, setSelectedUsers } from '../CreateMessage/createMessageSlice';
import type { SelectListUser } from '../CreateMessage/createMessageSlice/types';
import type { OptionItemProps } from '../../components/Options/Options';
import Options from '../../components/Options/Options';
import { getImageLink } from '../../utils/formatImageLink';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import UserProfileAvailabilityButton from '../../components/UserProfileAvailabilityButton/UserProfileAvailabilityButton';
import { toRem } from '../../utils/toRem';
import { checkIsAvailable } from '../../utils/helperFunctions';

type MemberSettingsProps = {
  isIamOkMemberSettings?: boolean;
  isOnCallAlertMemberSettings?: boolean;
};

export type MemberSettingsOptionsStateType = {
  isOpen: boolean;
  options?: OptionItemProps[];
};

// This needs serious refactoring
const MemberSettings = (props: MemberSettingsProps) => {
  const { isIamOkMemberSettings, isOnCallAlertMemberSettings } = props;

  const { id, memberID, imOkId, onCallAlertId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const userId = useAppSelector(selectUserId);
  const imOkDocument = useAppSelector(selectImOkDocument);
  const onCallAlertDocument = useAppSelector(selectOnCallAlertDocument);
  const member = useSelector((state: RootState) => selectMemberById(state, Number(memberID)));

  const [mapModalOpen, setMapModalOpen] = React.useState(false);
  const [isUserProfile, setIsUserProfile] = React.useState(false);
  const [optionsState, setOptionsState] = React.useState<MemberSettingsOptionsStateType>({
    isOpen: false,
  });

  React.useEffect(() => {
    let isMounted = true; // note mutable flag
    if (`${userId}` === memberID) {
      if (isMounted) setIsUserProfile(true);
    }
    if (isIamOkMemberSettings && imOkId) {
      dispatch(fetchImOkDocument(imOkId));
    }
    if (isOnCallAlertMemberSettings && onCallAlertId) {
      dispatch(fetchOnCallAlertDocument(+onCallAlertId));
    }

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true; // note mutable flag
    if (_.isUndefined(member) && id) {
      dispatch(fetchGroupDetail({ id: Number(id) }));
    }

    return () => {
      if (isMounted) setOptionsState({ isOpen: false });
      isMounted = false;
    };
  }, [member]);

  if (!member) {
    return <Loader />;
  }

  const addUserToCreateMessage = (selectedUser: SelectListUser) =>
    new Promise<void>(resolve => {
      batch(() => {
        dispatch(resetAll());
        dispatch(setActiveTab(ActiveTab.Users));
        dispatch(setSelectedUsers([selectedUser]));
      });
      resolve();
    });

  const actions: Record<string, UserAction[]> = {
    member: [
      {
        label: 'messages_createMessage',
        fireAction: async () => {
          await addUserToCreateMessage(
            mapGroupMemberToSelectUser(member, {
              title: '',
              isSelected: true,
              admin: undefined,
            })
          );

          navigate(`/createMessage/new`, {
            state: { prevPath: location.pathname, disableRecipientsChange: true },
          });
        },
      },
    ],
    ownProfile: [
      {
        label: 'groups_editProfile',
        fireAction: () => {
          navigate(`/profile`);
        },
      },
    ],
  };

  const isNeitherNullNorEmpty = (value: unknown) => {
    return !_.isNil(value) && !_.isEmpty(value);
  };

  const setOptionsOpen = (isOpen: boolean) => {
    setOptionsState(prev => ({ ...prev, isOpen }));
  };
  const emailField: DynamicFormFieldType = {
    id: 'email',
    visible: isNeitherNullNorEmpty(member?.userEmail),
    placeHolder: 'Email Address ',
    value: member?.userEmail || '',
    onClick: handleMemberSettingsEmailClick(
      member?.userEmail || '',
      setOptionsState,
      setOptionsOpen
    ),
  };
  const numberField: DynamicFormFieldType = {
    id: 'phoneNumber',
    visible: isNeitherNullNorEmpty(member?.phoneNumbers?.primary),
    placeHolder: 'Number Field ',
    value: member?.phoneNumbers?.primary || '',
    onClick: handleMemberSettingsPhoneClick(
      member?.phoneNumbers?.primary || '',
      setOptionsState,
      setOptionsOpen
    ),
  };
  const numberField2: DynamicFormFieldType = {
    visible: isNeitherNullNorEmpty(member?.phoneNumbers?.secondary),
    placeHolder: 'Number Field ',
    value: member?.phoneNumbers?.secondary || '',
    onClick: handleMemberSettingsPhoneClick(
      member?.phoneNumbers?.secondary || '',
      setOptionsState,
      setOptionsOpen
    ),
  };
  const additionalInfoField: DynamicFormFieldType = {
    id: 'additionalInfo',
    visible: isNeitherNullNorEmpty(member?.info),
    placeHolder: 'additionalInfo  ',
    value: member?.info || '',
  };

  const renderOnCallAlertWarning = () => {
    const hasPhone = onCallAlertDocument?.users.find(i => i.userId === +memberID!)?.hasPhoneNumber;
    if (!hasPhone) return <SOnCallAlertText>{translate('onCallAlert_noPhone')}</SOnCallAlertText>;
    return <></>;
  };
  const renderIamOkayLocation = () => {
    const locationName: string | 'NO_LOCATION' = getIamOkMemberLocationName(
      imOkDocument,
      memberID ? +memberID : undefined
    );

    if (locationName === 'NO_LOCATION')
      return (
        <SIamOkLocationContainer>
          <SSmallText $isLocationText>{translate('imOk_noLocation')}</SSmallText>
          <img src={NoLocationIcon} alt="NoLocationIcon" />
        </SIamOkLocationContainer>
      );
    return (
      <SIamOkLocationContainer $isLocation onClick={() => setMapModalOpen(true)}>
        <SSmallText $isLocationText $isLocation>
          {locationName}
        </SSmallText>
        <img src={LocationIcon} alt="LocationIcon" />
      </SIamOkLocationContainer>
    );
  };

  const renderMapModal = () => {
    if (!imOkDocument || !memberID) return <></>;
    const imOkLocation = getIamOkMemberLocation(imOkDocument, +memberID);

    if (!imOkLocation) return <></>;
    const { name, latitude, longitude, lastupdated } = imOkLocation;

    return (
      <>
        {mapModalOpen && location && (
          <Modal isOpen={mapModalOpen} setIsOpen={setMapModalOpen}>
            <MapModal
              address={name || ''}
              latitude={latitude ? +latitude : 0}
              longitude={longitude ? +longitude : 0}
              accuracy={imOkLocation.accuracy ?? undefined}
              type={member?.userName || ''}
              withoutRecalledLabel
              withDate
              recalled={true}
              sent={getNumbersBasedDateTextByLanguage({ dateText: lastupdated })}
            />
          </Modal>
        )}
      </>
    );
  };

  return (
    <SPage>
      <SProfilePictureWrapper>
        <ProfilePicture
          readyPhotoSource={getImageLink({
            imageName: member.photoFileName,
            size: 'medium',
          })}
          diameter={112}
        />
      </SProfilePictureWrapper>
      <SUserName id="username">{member?.userName}</SUserName>
      <SAvailabilityChipContainer>
        <UserProfileAvailabilityButton
          available={checkIsAvailable(member.unavailableDateTo)}
          unavailableDateTo={member?.unavailableDateTo}
        />
      </SAvailabilityChipContainer>

      <SMemberSettingsDynamicFormWrapper>
        <MemberSettingsDynamicForm
          fields={[additionalInfoField, emailField, numberField, numberField2]}
        />
      </SMemberSettingsDynamicFormWrapper>

      <SSmallText>
        {translate(`groups_lastVisited`)}{' '}
        {getNumbersBasedDateTextByLanguage({ dateText: member.lastAccess })}
      </SSmallText>

      {isOnCallAlertMemberSettings && onCallAlertDocument && renderOnCallAlertWarning()}
      {isIamOkMemberSettings && imOkDocument && renderIamOkayLocation()}
      <SFlexDiv />
      <ActionButtons actions={isUserProfile ? actions.ownProfile : actions.member} />
      {isIamOkMemberSettings && imOkDocument && renderMapModal()}
      {optionsState.options && (
        <Options
          items={optionsState.options}
          isOpen={optionsState.isOpen}
          setIsOpen={setOptionsOpen}
          setTabBar
        />
      )}
    </SPage>
  );
};

export default MemberSettings;

const SMemberSettingsDynamicFormWrapper = styled.div`
  margin: 1rem 0;
`;

const SFlexDiv = styled.div`
  flex: 1;
`;

export const SPage = styled(Page)`
  display: flex;
  flex-direction: column;
`;

const SOnCallAlertText = styled.p`
  font-family: 'Roboto-Regular';
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1.125rem;
  text-align: left;
  max-width: 75%;
  margin: 1rem auto 0 auto;
  color: ${palette.tartOrange};
  text-align: center;
`;

export const SSmallText = styled.p<{
  $isLocationText?: boolean;
  $isLocation?: boolean;
}>`
  color: ${palette.silver};
  font-family: 'Roboto-Regular';
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  text-align: center;

  :last-of-type {
    margin-top: 14px;
  }

  ${props =>
    props.$isLocation &&
    css`
      color: ${palette.honeyYellow};
    `}
  ${props =>
    props.$isLocationText &&
    css`
      :last-of-type {
        margin-top: 0px;
      }
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    `}
`;

const SIamOkLocationContainer = styled.div<{ $isLocation?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 1.875rem;
  max-width: 90%;
  margin: auto;
  margin-top: 1rem;

  ${props =>
    props.$isLocation &&
    css`
      cursor: pointer;
    `}
`;

export const SAvailabilityChipContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${toRem(4)};
`;

export const SProfilePictureWrapper = styled.div`
  margin-top: 1rem;
  align-self: center;
`;

export const SUserName = styled(SName)`
  margin-top: 12px;
  margin-bottom: 12px;
  text-align: center;
`;
